import moment from 'moment';
import DisplayHelper from './display_helper';

class SMSHelper {
  static getFirstSMS(smsMessageGroups) {
    return smsMessageGroups
      ?.map((group) => group.smsMessages)
      .flat()
      .map((message) => new Date(Date.parse(message.dateCreated + ' GMT-00:00')))
      .reduce((prevVal, curVal) => (prevVal < curVal ? prevVal : curVal), new Date());
  }

  static getLastSMS(smsMessageGroups) {
    return smsMessageGroups
      ?.map((group) => group.smsMessages)
      .flat()
      .map((message) => message.dateCreated)
      .reduce((prevVal, curVal) => (prevVal > curVal ? prevVal : curVal), null);
  }

  static getLastSMSGroup(smsMessageGroups) {
    if (!smsMessageGroups || !Array.isArray(smsMessageGroups) || smsMessageGroups.length === 0) {
      return null;
    }
    return smsMessageGroups.sort((a, b) => a.dateCreated.localeCompare(b.dateCreated))[smsMessageGroups.length - 1];
  }

  static getSMSOverallStatus(smsMessageGroups) {
    let smsStatus = null;
    let undeletedGroups = (smsMessageGroups || []).filter((e) => e).filter(({ dateDeleted }) => !dateDeleted);

    if (!undeletedGroups?.some((group) => group?.smsMessages?.length > 0)) {
      smsStatus = null;
    } else if (undeletedGroups?.some((s) => s.status === 'unsubscribed')) {
      smsStatus = 'unsubscribed';
    } else if (undeletedGroups?.some((s) => s.status === 'approved')) {
      smsStatus = 'approved';
    } else if (undeletedGroups?.some((s) => s.status === 'cancelled')) {
      smsStatus = 'cancelled';
    } else if (undeletedGroups?.some((s) => s.status === 'rejected')) {
      smsStatus = 'rejected';
    } else if (undeletedGroups?.some((s) => s.status === 'pending')) {
      smsStatus = 'pending';
    }
    return smsStatus;
  }

  static getSMSOverallStatusAccrossSteps(steps) {
    return this.getSMSOverallStatus(
      steps
        ?.map((step) => step?.docs?.map(({ smsMessageGroups }) => smsMessageGroups))
        ?.flat()
        ?.flat()
    );
  }

  static allSMSGroupsDeleted(smsMessageGroups) {
    if (smsMessageGroups) {
      return smsMessageGroups?.every(({ dateDeleted }) => dateDeleted);
    }
    return false;
  }

  static envPhoneNumbers() {
    return {
      local: ['+18555371789'],
      qa: ['+12166776616', '+14065102216'],
      prod: ['+19033428779', '+19252559373'],
      ci1: ['+18555371808'],
      ci2: ['+18555371808'],
      ci3: ['+18555371808'],
      ci4: ['+18555371808'],
      ci5: ['+18555371808'],
      ci6: ['+18555371808'],
      ci7: ['+18555371808'],
      ci8: ['+18555371808'],
      ci9: ['+18555371808'],
      ci0: ['+18555371789'],
    };
  }

  static calculateSMSMessageRowsForAudit(smsMessageGroups, stepDoc, destinationNumbers) {
    let envPhones = SMSHelper.envPhoneNumbers();
    return (
      smsMessageGroups &&
      smsMessageGroups
        .filter((a) => a.dateDeleted == null)
        // .sort((a, b) => a.dateCreated.localeCompare(b.dateCreated))
        .reduce((acc, { id, status, dateCreated, dateUpdated, smsMessages }) => {
          let customerSentMessages = smsMessages.filter(
            ({ destinationNumber, inboundMessageId }) =>
              Object.values(envPhones).some((n) => n.includes(destinationNumber)) &&
              (!inboundMessageId || !inboundMessageId.includes('admin-override:'))
          );

          let fDateCreated = moment.utc(dateCreated, 'YYYY-MM-DD hh:mm:ss'); //.format('MM/DD/YY hh:mm');
          let fDateUpdated = moment.utc(dateUpdated || dateCreated, 'YYYY-MM-DD hh:mm:ss'); //.format('MM/DD/YY hh:mm');
          function getPendingMessage() {
            let disbursementNumber = { PaymentAuthorizationForm1: 'first', PaymentAuthorizationForm2: 'second' }[
              stepDoc.externalSku
            ];
            acc.push({
              id: id + smsMessageGroups.length,
              dateCreated: fDateCreated,
              status,
              message: [
                `Sent a text message to ${destinationNumbers
                  .map(DisplayHelper.formatPhoneNumber)
                  .join(
                    ' & '
                  )} requesting authorization for the ${disbursementNumber} disbursement on ${DisplayHelper.formatDateV2(
                  dateCreated
                )}. If the customer hasn't received the message in 60 seconds, please use the resend functionality below`,
              ],
              // className: 'sms-accepted-green',
            });
          }

          let wasAdminOverride = (smsMessages || []).some(
            ({ inboundMessageId }) =>
              typeof inboundMessageId === 'string' && inboundMessageId.includes('admin-override:')
          );

          (customerSentMessages || []).forEach(({ dateCreated, messageBody }) => {
            let customerMessageDateCreated = moment.utc(dateCreated, 'YYYY-MM-DD hh:mm:ss'); //.format('MM/DD/YY hh:mm');
            acc.push({
              id: Math.random(),
              dateCreated: customerMessageDateCreated,
              status,
              customerDefinedMessage: true,
              message: `Customer replied with "${messageBody}"`,
              // className: 'sms-reject-red',
            });
          });

          getPendingMessage();
          if (status !== 'pending') {
            acc.push({
              id,
              dateCreated: fDateUpdated,
              status,
              message: {
                rejected: `Customer has denied payment via text message`,
                cancelled: `Installer has temporarily cancelled funding request`,
                unsubscribed: `Customer unsubscribed from text messages; sign with alternative method to continue`,
                approved: wasAdminOverride
                  ? `Auto accepted after 24 hours`
                  : `Customer has accepted payment via text message`,
              }[status],
              // className: 'sms-reject-red',
            });
          }
          return acc;
        }, [])
        .sort((a, b) => a.dateCreated.unix() - b.dateCreated.unix())
        .map((a) => {
          a.dateCreated = a.dateCreated.local().format('MM/DD/YY HH:mm');
          return a;
        })
    );
  }

  static hasBeen24Hours(date) {
    const MS_IN_A_DAY = 24 * 60 * 60 * 1000;
    try {
      let future = new Date(date.getTime() + MS_IN_A_DAY);
      return future.getTime() < Date.now();
    } catch (e) {
      return true;
    }
  }

  static getDestinationNumbers = (smsMessageGroups) => {
    return [
      ...new Set(
        smsMessageGroups
          ?.map((group) =>
            group.smsMessages
              // first message when creating group has no origination number but has 'pre_send' we use this to capture the init of the group
              .filter((message) => message.originationNumber === 'pre_send')
              .map((message) => message.destinationNumber)
          )
          .flat()
      ),
    ];
  };
}

export default SMSHelper;
